import React, { useEffect, useRef, useState } from 'react';

import type { FiltrosDashboard } from '../../../../api/contac/contacDashboardApi';
import '../../../../components/modal/contac/filtro/ModalFiltro.css';
import '../../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Filtros do Dashboard da Associação (at-86), no padrão do Contac / at-61.
 *
 * ⚠️ MONTAGEM CONDICIONAL (`{aberto && <Modal/>}`), nunca prop `isOpen` com `return null`: é o
 * `useState` inicial que passa a ser a única fonte do rascunho, então "Cancelar" descarta de
 * verdade. O desenho antigo (modal sempre montado + `useEffect` recopiando as props) deixou um
 * rascunho descartado sobreviver para a abertura seguinte na at-61.
 *
 * ⚠️ O modal RECEBE o que está aplicado (`filtrosAplicados`). Como não há resumo dos filtros fora
 * dele (decisão do Contac), reabrir é a ÚNICA forma de conferir o que está valendo — um modal que
 * reabrisse em branco faria quem confere ler "nenhum filtro" numa tela filtrada, e um "Aplicar" em
 * seguida limparia tudo em silêncio (BUG-V43).
 */

interface Props {
  filtrosAplicados: FiltrosDashboard;
  /** Bases presentes nos dados — evita oferecer uma base que não existe na carteira. */
  basesDisponiveis: string[];
  /** Categorias presentes nos dados, já normalizadas pelo backend. */
  categoriasDisponiveis: string[];
  onAplicar: (filtros: FiltrosDashboard) => void;
  onFechar: () => void;
  sidebarRef?: React.RefObject<HTMLElement>;
}

const DashboardFiltroModal: React.FC<Props> = ({
  filtrosAplicados,
  basesDisponiveis,
  categoriasDisponiveis,
  onAplicar,
  onFechar,
  sidebarRef,
}) => {
  const [dataInicio, setDataInicio] = useState(filtrosAplicados.dataInicio ?? '');
  const [dataFim, setDataFim] = useState(filtrosAplicados.dataFim ?? '');
  const [baseTag, setBaseTag] = useState(filtrosAplicados.baseTag ?? '');
  const [codigoRegional, setCodigoRegional] = useState(filtrosAplicados.codigoRegional ?? '');
  const [categoria, setCategoria] = useState(filtrosAplicados.categoria ?? '');

  const conteudoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (conteudoRef.current?.contains(alvo)) return;
      // Sem esta guarda, clicar na sidebar fecharia o modal — e o teste do fechamento passaria
      // em falso sem a ref (medido em 18/08/2026).
      if (sidebarRef?.current?.contains(alvo)) return;
      onFechar();
    };

    const escape = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };

    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', escape);
    };
  }, [onFechar, sidebarRef]);

  const aplicar = () => {
    onAplicar({
      dataInicio: dataInicio || undefined,
      dataFim: dataFim || undefined,
      baseTag: baseTag || undefined,
      codigoRegional: codigoRegional.trim() || undefined,
      categoria: categoria || undefined,
    });
  };

  const limpar = () => {
    setDataInicio('');
    setDataFim('');
    setBaseTag('');
    setCodigoRegional('');
    setCategoria('');
  };

  return (
    <div className="modal-filtro-overlay">
      <div className="modal-filtro-content" ref={conteudoRef} role="dialog" aria-label="Filtros do dashboard">
        <div className="filtro-modal-card">
          <div className="filtro-modal-header">
            <div>
              <div className="filtro-modal-title">Filtros</div>
              <div className="filtro-modal-subtitle">
                Os filtros valem para todas as abas. Deixe em branco para ver o período padrão e a
                carteira inteira.
              </div>
            </div>
            <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar filtros">
              &times;
            </button>
          </div>

          <div className="filtro-modal-body">
            <div className="filtro-section">
              <div className="filtro-section-header">
                <div className="filtro-section-title">Período</div>
              </div>
              <div className="filtro-grid-2">
                <div className="filtro-field">
                  <label htmlFor="do-data-inicio">Data inicial</label>
                  <input
                    id="do-data-inicio"
                    type="date"
                    className="premium-text-input"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                  />
                </div>
                <div className="filtro-field">
                  <label htmlFor="do-data-fim">Data final</label>
                  <input
                    id="do-data-fim"
                    type="date"
                    className="premium-text-input"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                  />
                </div>
              </div>
              <div className="filtro-field-hint">
                Os dados financeiros ficam disponíveis com dois meses de defasagem. Sem período
                escolhido, a tela usa as 12 competências mais recentes disponíveis.
              </div>
            </div>

            <div className="filtro-section">
              <div className="filtro-section-header">
                <div className="filtro-section-title">Recorte</div>
              </div>
              <div className="filtro-grid-2">
                <div className="filtro-field">
                  <label htmlFor="do-base">Base</label>
                  <div className="select-wrapper">
                    <select id="do-base" value={baseTag} onChange={(e) => setBaseTag(e.target.value)}>
                      <option value="">Todas as bases</option>
                      {basesDisponiveis.map((base) => (
                        <option key={base} value={base}>
                          {base}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="filtro-field">
                  <label htmlFor="do-categoria">Categoria</label>
                  <div className="select-wrapper">
                    <select
                      id="do-categoria"
                      value={categoria}
                      onChange={(e) => setCategoria(e.target.value)}
                    >
                      <option value="">Todas as categorias</option>
                      {categoriasDisponiveis.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="filtro-grid-2" style={{ marginTop: 14 }}>
                <div className="filtro-field">
                  <label htmlFor="do-regional">Código da regional</label>
                  <input
                    id="do-regional"
                    type="text"
                    inputMode="numeric"
                    className="premium-text-input"
                    placeholder="Ex.: 3"
                    value={codigoRegional}
                    onChange={(e) => setCodigoRegional(e.target.value)}
                  />
                </div>
              </div>

              <div className="filtro-field-hint">
                Regional e categoria valem na aba Frota &amp; Associados. Na Visão Geral eles não se
                aplicam — o valor é cobrado por boleto, e um boleto pode cobrir veículos de
                regionais e categorias diferentes; aquela aba avisa quando isso acontece.
              </div>
            </div>
          </div>

          <div className="filtro-modal-footer">
            <button type="button" className="filtro-reset-btn" onClick={limpar}>
              Limpar filtros
            </button>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="filtro-btn-secondary" onClick={onFechar}>
                Cancelar
              </button>
              <button type="button" className="filtro-btn-primary" onClick={aplicar}>
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardFiltroModal;
